/**
 * wc-cart-reorder-guard.js
 *
 * Cart-level reorder detection and routing for Wholesale Carabiners.
 *
 * Four detection paths:
 *   1. Quantity change on tiered-pricing items  -> redirect to PDP modal
 *   2. reorder-via-form items (needs_reorder_form) -> "Request Reorder" banner
 *   3. Stale reorder items (tiered, no Easefy)    -> inline reorder banner with PDP link
 *   4. Buy Again failure from new customer accounts (empty cart) -> fallback notice
 *
 * Also fixes Easify add-on line shift bug on cart delete.
 */
(function () {
  'use strict';

  // ── Helper: find Mold line-item keys in the cart ──────────────────
  function getMoldLineItemKeys() {
    var keys = [];
    document.querySelectorAll('tr.cart-item').forEach(function (row) {
      var title = (row.getAttribute('data-product-title') || '').toLowerCase();
      if (title.indexOf('mold') >= 0) {
        var k = row.getAttribute('data-line-item-key');
        if (k) keys.push(k);
      }
    });
    return keys;
  }

  function removeLineItems(keys, callback) {
    if (!keys.length) { callback(); return; }
    var key = keys.shift();
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(function () { removeLineItems(keys, callback); })
    .catch(function () { removeLineItems(keys, callback); });
  }

  // -------------------------------------------------------------------
  // FIX: Cart delete using stable item key instead of shifting line index
  // Easify adds hidden add-on lines after page render, which shifts the
  // line numbers so index-based deletes get "no valid id or line parameter".
  // We intercept the trash-icon click and POST with the item key instead.
  // -------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-remove-item');
    if (!btn) return;
    var key = btn.dataset.key;
    if (!key) return; // fallback: let theme handle it normally

    e.preventDefault();
    e.stopImmediatePropagation();

    var row = btn.closest('.cart-item');
    var cartItems = document.querySelector('cart-items');
    var line = btn.dataset.index;
    if (cartItems && cartItems.enableLoading) cartItems.enableLoading(line);

    // Delete the item using its stable key, then reload
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(function() {
      window.location.reload();
    })
    .catch(function() {
      window.location.reload();
    });

  }, true /* capture */);

  function showRedirectBanner(productUrl) {
    // Remove any previous modal
    var prev = document.getElementById('wc-reorder-overlay');
    if (prev) prev.remove();

    // Overlay
    var overlay = document.createElement('div');
    overlay.id = 'wc-reorder-overlay';
    overlay.style.cssText = [
      'position:fixed;top:0;left:0;width:100%;height:100%;',
      'background:rgba(0,0,0,0.55);z-index:999998;'
    ].join('');

    // Modal box
    var modal = document.createElement('div');
    modal.style.cssText = [
      'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);',
      'z-index:999999;background:#fff;',
      'width:360px;max-width:90vw;',
      'padding:36px 32px 28px;',
      'box-shadow:0 8px 32px rgba(0,0,0,0.22);',
      'font-family:inherit;text-align:center;'
    ].join('');

    modal.innerHTML =
      '<p style="font-size:18px;font-weight:700;margin:0 0 12px;color:#1d1d1d;">' +
        'Quantity Changed' +
      '</p>' +
      '<p style="font-size:14px;color:#444;margin:0 0 24px;line-height:1.6;">' +
        'Our pricing varies by quantity. Please visit the product page so we can calculate the correct price for you.' +
      '</p>' +
      '<a href="' + productUrl + '" ' +
        'style="display:block;background:#1A66D2;color:#fff;text-decoration:none;' +
               'font-weight:700;font-size:15px;padding:13px 20px;' +
               'margin-bottom:12px;cursor:pointer;">' +
        'Go to Product Page' +
      '</a>' +
      '<button id="wc-reorder-cancel" ' +
        'style="display:block;width:100%;background:#fff;border:2px solid #1A66D2;' +
               'color:#1A66D2;font-weight:700;font-size:15px;padding:11px 20px;' +
               'cursor:pointer;">' +
        'Stay in Cart' +
      '</button>';

    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    function dismiss() {
      overlay.remove();
      modal.remove();
    }

    document.getElementById('wc-reorder-cancel').addEventListener('click', dismiss);
    overlay.addEventListener('click', dismiss);
  }

  // -------------------------------------------------------------------
  // Detect the original order number from document.referrer.
  // -------------------------------------------------------------------
  function detectOriginalOrderRef() {
    try {
      var stashed = sessionStorage.getItem('wc_last_viewed_order_name');
      if (stashed) return stashed;
    } catch (e) {}

    var ref = document.referrer || '';
    if (!ref) return '';
    var match = ref.match(/\/account\/orders\/([^\/\?#]+)/i);
    if (match) {
      return '#' + match[1].substring(0, 12);
    }
    return '';
  }

  // -------------------------------------------------------------------
  // Handle reorder-via-form items: products tagged "reorder-via-form"
  // that can't be auto-reordered. Show a dedicated banner directing
  // to the reorder request form.
  // -------------------------------------------------------------------
  function handleReorderFormItems() {
    var formRows = document.querySelectorAll('tr[data-needs-reorder-form="true"]');
    if (!formRows.length) return;

    var orderRef = detectOriginalOrderRef();

    // Collect all items so we can pass a summary to the form
    var itemSummaries = [];
    formRows.forEach(function (row) {
      var title = row.dataset.productTitle || '';
      var variant = row.dataset.variantTitle || '';
      var sku = row.dataset.productSku || '';
      var qtyInput = row.querySelector('.qty-input__input');
      var qty = qtyInput ? qtyInput.value : '?';
      var summary = title;
      if (variant && variant !== 'Default Title') summary += ' (' + variant + ')';
      if (sku) summary += ' [' + sku + ']';
      summary += ' x ' + qty;
      itemSummaries.push(summary);
    });

    var itemsParam = encodeURIComponent(itemSummaries.join(' | '));
    var orderParam = orderRef ? encodeURIComponent(orderRef) : '';
    var formUrl = '/pages/reorder-request?order=' + orderParam + '&items=' + itemsParam;

    formRows.forEach(function (row) {
      if (row.querySelector('.wc-reorder-form-banner')) return;

      var lineItemKey = row.dataset.lineItemKey;

      // Dim price + disable qty controls
      var priceEls = row.querySelectorAll('.price__current');
      priceEls.forEach(function (el) { el.style.opacity = '0.35'; });
      var qtyInput = row.querySelector('.qty-input__input');
      if (qtyInput) qtyInput.disabled = true;
      var qtyBtns = row.querySelectorAll('.qty-input__btn');
      qtyBtns.forEach(function (btn) { btn.disabled = true; });

      // Build banner
      var banner = document.createElement('div');
      banner.className = 'wc-reorder-form-banner';
      banner.style.cssText = [
        'margin:12px 0 4px;padding:16px 18px;',
        'border:2px solid #1A66D2;background:#EBF2FC;',
        'border-radius:10px;text-align:center;'
      ].join('');

      var heading = document.createElement('div');
      heading.style.cssText = 'font-size:15px;font-weight:700;color:#000;margin-bottom:6px;';
      heading.textContent = '\uD83D\uDCDD Reorder Request Needed';

      var message = document.createElement('div');
      message.style.cssText = 'font-size:13px;color:#444;line-height:1.5;margin-bottom:14px;';
      message.textContent = 'This item needs to be processed as a manual reorder so we can match your original specifications exactly. Click below to submit a quick reorder request \u2014 we will confirm everything with a digital proof before invoicing.';

      banner.appendChild(heading);
      banner.appendChild(message);

      var link = document.createElement('a');
      link.href = formUrl;
      link.style.cssText = [
        'display:inline-block;background:#1A66D2;color:#fff;text-decoration:none;',
        'font-weight:700;font-size:14px;padding:11px 28px;cursor:pointer;',
        'border-radius:30px;'
      ].join('');
      link.textContent = 'Request Reorder';

      link.addEventListener('click', function (e) {
        e.preventDefault();
        link.textContent = '\u23F3 Clearing cart\u2026';
        link.style.pointerEvents = 'none';

        // Remove the stale item + any Mold items before redirecting
        var keysToRemove = getMoldLineItemKeys();
        if (lineItemKey) keysToRemove.unshift(lineItemKey);

        if (keysToRemove.length) {
          removeLineItems(keysToRemove, function () {
            window.location.href = formUrl;
          });
        } else {
          window.location.href = formUrl;
        }
      });

      banner.appendChild(link);

      var detailsCell = row.querySelector('.cart-item__details');
      if (detailsCell) {
        detailsCell.appendChild(banner);
      }
    });

    // Disable the normal checkout path
    disableCheckoutButtons();
  }

  // -------------------------------------------------------------------
  // Disable checkout buttons when reorder form items are present.
  // -------------------------------------------------------------------
  function disableCheckoutButtons() {
    function isReorderFormPresent() {
      return !!document.querySelector('tr[data-needs-reorder-form="true"]');
    }

    function applyDisable() {
      if (!isReorderFormPresent()) return;

      var mainBtns = document.querySelectorAll(
        'button[name="checkout"], input[name="checkout"], .cart__checkout-button, button.btn--primary[name="checkout"]'
      );
      mainBtns.forEach(function (btn) {
        btn.dataset.wcReorderDisabled = '1';
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.style.opacity = '0.45';
        btn.style.cursor = 'not-allowed';
        btn.style.pointerEvents = 'none';
        btn.setAttribute(
          'title',
          'Please use "Request Reorder" above to submit your reorder.'
        );
      });

      var dynamicContainers = document.querySelectorAll(
        '.dynamic-cart-btns, .additional-checkout-buttons, .dynamic-checkout__buttons, [data-shopify="dynamic-checkout-cart"], .shopify-payment-button'
      );
      dynamicContainers.forEach(function (el) {
        el.dataset.wcReorderDisabled = '1';
        el.style.opacity = '0.35';
        el.style.pointerEvents = 'none';
        el.style.filter = 'grayscale(60%)';
      });

      // Inject notice above cart footer buttons (once)
      if (!document.getElementById('wc-reorder-checkout-notice')) {
        var target = document.querySelector(
          '.cart__ctas, .cart__footer, .cart-summary, .cart__summary, .main-cart-footer'
        );
        if (target) {
          var notice = document.createElement('div');
          notice.id = 'wc-reorder-checkout-notice';
          notice.style.cssText = [
            'background:#EBF2FC;border:none;',
            'padding:12px 16px;margin-bottom:14px;',
            'font-size:12px;color:#000;line-height:1.5;',
            'text-align:center;border-radius:8px;'
          ].join('');
          notice.innerHTML =
            '<strong>\u26A0 Checkout disabled:</strong> One or more items in your cart need to be reordered manually. Please click <strong>Request Reorder</strong> on the item above to submit your request.';
          target.insertBefore(notice, target.firstChild);
        }
      }
    }

    // Document-level capture-phase click interceptor
    document.addEventListener('click', function (e) {
      if (!isReorderFormPresent()) return;

      var target = e.target;
      if (!target || !target.closest) return;

      var checkoutHit = target.closest(
        'button[name="checkout"], input[name="checkout"], ' +
        '.cart__checkout-button, button.btn--primary[name="checkout"], ' +
        '.dynamic-cart-btns, .additional-checkout-buttons, ' +
        '.dynamic-checkout__buttons, .shopify-payment-button'
      );

      if (checkoutHit) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        var banner = document.querySelector('.wc-reorder-form-banner');
        if (banner) {
          banner.style.transition = 'box-shadow 0.3s';
          banner.style.boxShadow = '0 0 0 4px rgba(26,102,210,0.45)';
          banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function () { banner.style.boxShadow = ''; }, 1200);
        }
      }
    }, true /* capture phase */);

    // Block the cart form submit
    document.addEventListener('submit', function (e) {
      if (!isReorderFormPresent()) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (form.querySelector('button[name="checkout"], input[name="checkout"]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true /* capture phase */);

    applyDisable();

    var attempts = 0;
    var retry = setInterval(function () {
      applyDisable();
      attempts++;
      if (attempts >= 15) clearInterval(retry);
    }, 400);

    if (window.MutationObserver) {
      var observer = new MutationObserver(function () { applyDisable(); });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { observer.disconnect(); }, 15000);
    }
  }

  // -------------------------------------------------------------------
  // Handle stale reorders: tiered pricing items with no Easefy props.
  // -------------------------------------------------------------------
  function handleStaleReorders() {
    var staleRows = document.querySelectorAll('tr[data-stale-reorder="true"]');
    if (!staleRows.length) return;

    var orderRef = detectOriginalOrderRef();

    staleRows.forEach(function (row, idx) {
      if (row.querySelector('.wc-stale-reorder-banner')) return;

      var titleLink = row.querySelector('a.cart-item__title[href]');
      var productUrl = titleLink ? titleLink.href : '';
      var lineItemKey = row.dataset.lineItemKey;

      var priceEls = row.querySelectorAll('.price__current');
      priceEls.forEach(function (el) { el.style.opacity = '0.35'; });

      var qtyInput = row.querySelector('.qty-input__input');
      if (qtyInput) qtyInput.disabled = true;
      var qtyBtns = row.querySelectorAll('.qty-input__btn');
      qtyBtns.forEach(function (btn) { btn.disabled = true; });

      var banner = document.createElement('div');
      banner.className = 'wc-stale-reorder-banner';
      banner.style.cssText = [
        'margin:12px 0 4px;padding:16px 18px;',
        'border:2px solid #1A66D2;background:#EBF2FC;',
        'text-align:left;'
      ].join('');

      var heading = document.createElement('div');
      heading.style.cssText = 'font-size:15px;font-weight:700;color:#0D47A1;margin-bottom:6px;text-align:center;';
      heading.textContent = '\uD83D\uDD04 Reorder' + (orderRef ? ' from Order ' + orderRef : '');

      var priceWarning = document.createElement('div');
      priceWarning.style.cssText = 'font-size:12px;color:#0D47A1;font-weight:600;margin-bottom:10px;text-align:center;';
      priceWarning.textContent = '\u26A0 Price shown is the base price and may not reflect your quantity discount.';

      var message = document.createElement('div');
      message.style.cssText = 'font-size:13px;color:#444;line-height:1.5;margin-bottom:12px;text-align:center;';
      message.textContent = 'We have your original design on file. Visit the product page to confirm your quantity and get the correct price.';

      banner.appendChild(heading);
      banner.appendChild(priceWarning);
      banner.appendChild(message);

      var notesLabel = document.createElement('label');
      notesLabel.style.cssText = 'display:block;font-size:13px;font-weight:700;color:#1d1d1d;margin-bottom:6px;';
      notesLabel.textContent = 'Any changes to your design? (leave blank for an exact reorder)';
      var notesId = 'wc-reorder-notes-' + idx;
      notesLabel.setAttribute('for', notesId);

      var notesInput = document.createElement('textarea');
      notesInput.id = notesId;
      notesInput.rows = 3;
      notesInput.placeholder = 'e.g. Change text to "Best Dad Ever" instead of original text, or any other changes.';
      notesInput.style.cssText = [
        'width:100%;box-sizing:border-box;',
        'border:1px solid #90CAF9;padding:10px 12px;',
        'font-family:inherit;font-size:13px;color:#1d1d1d;',
        'margin-bottom:14px;resize:vertical;'
      ].join('');

      banner.appendChild(notesLabel);
      banner.appendChild(notesInput);

      var btnWrap = document.createElement('div');
      btnWrap.style.cssText = 'text-align:center;';

      if (productUrl) {
        var link = document.createElement('a');
        link.href = productUrl;
        link.style.cssText = [
          'display:inline-block;background:#1A66D2;color:#fff;text-decoration:none;',
          'font-weight:700;font-size:14px;padding:11px 28px;cursor:pointer;'
        ].join('');
        link.textContent = 'Go to Product Page';

        link.addEventListener('click', function (e) {
          e.preventDefault();

          var reorderData = {
            isReorder: true,
            orderRef: orderRef,
            changeNotes: notesInput.value || '',
            timestamp: Date.now()
          };
          try {
            sessionStorage.setItem('wc_reorder_data', JSON.stringify(reorderData));
          } catch (err) {}

          link.textContent = '\u23F3 Removing old item\u2026';
          link.style.pointerEvents = 'none';

          var keysToRemove = getMoldLineItemKeys();
          if (lineItemKey) keysToRemove.unshift(lineItemKey);

          if (keysToRemove.length) {
            removeLineItems(keysToRemove, function () {
              window.location.href = productUrl;
            });
          } else {
            window.location.href = productUrl;
          }
        });

        btnWrap.appendChild(link);
      }

      banner.appendChild(btnWrap);

      var detailsCell = row.querySelector('.cart-item__details');
      if (detailsCell) {
        detailsCell.appendChild(banner);
      }
    });
  }

  // -------------------------------------------------------------------
  // Handle "Buy Again" failures from new customer accounts — imported
  // Magento orders where items can't be added.
  // -------------------------------------------------------------------
  function handleBuyAgainFailure() {
    if (!/\/cart(\?|$|\/$)/.test(window.location.pathname + window.location.search)) {
      if (window.location.pathname !== '/cart') return;
    }

    if (document.getElementById('wc-buy-again-fallback-notice')) return;

    var cartIsEmpty = !!document.querySelector('.cart-empty, .cart.is-empty, .js-cart-empty');
    var cartRows = document.querySelectorAll('tr.cart-item');
    if (!cartIsEmpty && cartRows.length > 0) return;

    var params = new URLSearchParams(window.location.search);
    var fromAttr = params.get('attributes[from]') || params.get('attributes%5Bfrom%5D') || '';
    var ref = document.referrer || '';
    var fromAccounts = fromAttr === 'new-customer-accounts' ||
                       ref.indexOf('account.') >= 0 ||
                       ref.indexOf('/account') >= 0 ||
                       ref.indexOf('/orders') >= 0 ||
                       sessionStorage.getItem('wc_buy_again_attempted') === '1';

    if (!fromAccounts) return;
    sessionStorage.removeItem('wc_buy_again_attempted');

    showEmptyCartFallbackNotice();
  }

  function showEmptyCartFallbackNotice() {
    var target =
      document.querySelector('.js-cart-empty') ||
      document.querySelector('cart-items') ||
      document.querySelector('.cart-empty') ||
      document.querySelector('main') ||
      document.body;
    if (!target) return;

    var notice = document.createElement('div');
    notice.id = 'wc-buy-again-fallback-notice';
    notice.style.cssText = [
      'background:#EBF2FC;padding:24px 28px;',
      'margin:20px auto;max-width:640px;',
      'text-align:center;font-family:inherit;',
      'border-radius:10px;border:2px solid #1A66D2;'
    ].join('');

    var heading = document.createElement('div');
    heading.style.cssText = 'font-size:18px;font-weight:700;color:#000;margin-bottom:10px;';
    heading.textContent = '\uD83D\uDD04 Reordering an older order?';

    var message = document.createElement('div');
    message.style.cssText = 'font-size:14px;color:#444;line-height:1.6;margin-bottom:16px;';
    message.innerHTML =
      'It looks like you tried to reorder a past order, but the items could not be added directly. ' +
      'This is common for orders placed on our <strong>previous website</strong> or for custom items. ' +
      'No problem \u2014 we have your full history on file and can set up your reorder manually.';

    var btn = document.createElement('a');
    btn.href = '/pages/reorder-request';
    btn.style.cssText = [
      'display:inline-block;background:#1A66D2;color:#fff;text-decoration:none;',
      'font-weight:700;font-size:14px;padding:12px 32px;cursor:pointer;',
      'border-radius:30px;'
    ].join('');
    btn.textContent = 'Request a Reorder';

    var helpText = document.createElement('div');
    helpText.style.cssText = 'font-size:12px;color:#888;margin-top:14px;line-height:1.5;';
    helpText.innerHTML =
      'Or call us at <strong>877-204-9511</strong> and mention the order number from your email history.';

    notice.appendChild(heading);
    notice.appendChild(message);
    notice.appendChild(btn);
    notice.appendChild(helpText);

    if (target.firstChild) {
      target.insertBefore(notice, target.firstChild);
    } else {
      target.appendChild(notice);
    }
  }

  function runHandlers() {
    handleReorderFormItems();
    handleStaleReorders();
    handleBuyAgainFailure();
  }

  // Run on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runHandlers);
  } else {
    runHandlers();
  }

  // Also run after Shopify section re-renders
  document.addEventListener('shopify:section:load', runHandlers);

  // Use capture phase so this fires BEFORE cart-items.js processes the change
  document.addEventListener('change', function (e) {
    var input = e.target;

    if (!input.classList.contains('qty-input__input')) return;

    var row = input.closest('.cart-item');
    if (!row) return;

    if (row.dataset.hasTiers !== 'true') return;

    var initialQty = parseInt(input.dataset.initialValue, 10);
    var newQty     = parseInt(input.value, 10);

    if (newQty === 0 || newQty === initialQty) return;

    e.stopImmediatePropagation();

    input.value = initialQty;
    var qtyEl = input.closest('quantity-input');
    if (qtyEl) qtyEl.currentQty = String(initialQty);

    var titleLink = row.querySelector('a.cart-item__title[href]');
    if (!titleLink) return;

    showRedirectBanner(titleLink.href);

  }, true /* capture */);

}());
